import React from "react";
import { Autocomplete, Box, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { dropdownSlotProps } from "../../styles/dropdown";
import { ChartOption, ChartOptionItem } from "../../../excel/charts/api";
import ChartIconImage from "./ChartIconImage";

interface ChartOptionControlsProps {
  options: Record<string, ChartOption>;
  /** Current key per option, held by the caller so it can revert on failure. */
  values: Record<string, string>;
  onChange: (option: ChartOption, optionItem: ChartOptionItem) => void;
  disabled?: boolean;
}

const renderOptionInput = (
  option: ChartOption,
  selectedKey: string,
  onChange: (value: string) => void,
  disabled?: boolean
) => {
  switch (option.type) {
    case "boolean":
      return (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <Typography sx={{ fontSize: "12px" }}>{option.label}</Typography>
          <Switch
            checked={selectedKey === "true"}
            onChange={(event) => onChange(event.target.checked ? "true" : "false")}
            size="small"
            disabled={disabled}
          />
        </Box>
      );
    case "toggle":
      return (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", gap: 1 }}>
          <Typography sx={{ fontSize: "12px" }}>{option.label}</Typography>
          <ToggleButtonGroup
            value={selectedKey}
            exclusive
            onChange={(_event, newValue) => newValue && onChange(newValue)}
            size="small"
            disabled={disabled}
            sx={{ margin: 0, height: "30px" }}
          >
            {Object.values(option.optionItems).map((item) => (
              <ToggleButton
                key={item.key}
                value={item.key}
                aria-label={item.label || item.key}
                sx={{ fontSize: "12px", padding: "5px", fontWeight: 700, minWidth: "30px", textTransform: "none" }}
              >
                {item.icon && <ChartIconImage icon={item.icon} />}
                {item.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      );
    case "dropDown": {
      const items = Object.values(option.optionItems);
      // A dropdown with one choice is not a choice.
      if (items.length === 1) return null;
      return (
        <Autocomplete
          multiple={false}
          options={items}
          value={selectedKey ? option.optionItems[selectedKey] || null : null}
          onChange={(_event, newValue) => onChange(newValue?.key || "")}
          getOptionLabel={(item) => item.label}
          disabled={disabled}
          renderInput={(params) => <TextField {...params} label={option.label} size="small" variant="outlined" />}
          slotProps={dropdownSlotProps}
        />
      );
    }
    case "text":
      return (
        <TextField
          label={option.label}
          value={selectedKey || ""}
          onChange={(event) => onChange(event.target.value)}
          size="small"
          variant="outlined"
          fullWidth
          disabled={disabled}
        />
      );
    default:
      return null;
  }
};

/** Renders a chart's options: one labelled input per option, driven by the option's declared type. */
export const ChartOptionControls: React.FC<ChartOptionControlsProps> = ({ options, values, onChange, disabled }) => (
  <>
    {Object.values(options).map((option) => {
      const input = renderOptionInput(
        option,
        values[option.key],
        (value) => onChange(option, option.optionItems[value]),
        disabled
      );
      return input && <Box key={option.key}>{input}</Box>;
    })}
  </>
);

export default ChartOptionControls;
